import { Component, Element, Host, JSX, Prop, State, Watch, h } from '@stencil/core';

import LeftArrow from '../../../../assets/icon/left-arrow.svg';
import RightArrow from '../../../../assets/icon/right-arrow.svg';

@Component({
  tag: 'carousel-buttons',
  styleUrl: 'carousel-buttons.scss',
  shadow: false,
  scoped: true
})
export class CarouselButtons {
  @Element() private hostElement: HTMLDlsButtonElement;
  @Prop() public numberOfProducts: number;
  @Prop() public focusedCardIndex?: number;
  @Prop() public showPagination?: boolean;

  @State() public viewPortWidth = 0;
  @State() public cardsContainerWidth = 0;
  @State() public cardWidth = 0;
  @State() public overlayWidth = 0;
  @State() public cardsFit = false;
  @State() public scrollLeft = 0;
  @State() public firstCardFits = false;
  @State() public lastCardFits = false;

  private leftButton: HTMLElement;
  private rightButton: HTMLElement;

  private changeVisibility = (button: HTMLElement, visible: boolean): void => {
    visible ? button.classList.remove('hide') : button.classList.add('hide');
  };

  private focusInitialCard = (): void => {
    const focusedCard = this.hostElement.querySelectorAll('.card__wrapper')[this.focusedCardIndex];
    const viewportWidth = document.getElementById('carousel-wrapper').clientWidth;
    const shift = focusedCard?.getBoundingClientRect().x + focusedCard?.clientWidth / 2 - viewportWidth / 2;
    const carouselScroller = document.querySelector('#carousel-wrapper');
    carouselScroller.scrollBy(shift, 0);
    this.scrollLeft = carouselScroller.scrollLeft;
  };

  private calculateAndPositionCards = (): void => {
    const carouselWrapper = document.getElementById('carousel-wrapper');
    if (!carouselWrapper) return;
    const containerBounding = carouselWrapper.getBoundingClientRect();
    const viewportWidth = containerBounding.width;

    const cardsContainer = document.getElementById('card-container');
    const cardsContainerWidth = cardsContainer.clientWidth;

    const allCards = document.getElementsByClassName('card__wrapper');
    const cardWidth = allCards[0].getBoundingClientRect().width;

    const cardMargin = 32;
    const cardsFit = viewportWidth >= cardsContainerWidth;
    let fitCardsWidth = 0;
    let numberOfCards = 1;
    let cardsContainerMargin = 0;
    let edgeCardVisibleWidth = 0;
    if (cardsFit) {
      cardsContainerMargin = (viewportWidth - cardsContainerWidth) / 2;
    } else {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const cardsWidthWithMargins = numberOfCards * cardWidth + (numberOfCards + 1) * cardMargin;
        if (cardsWidthWithMargins < viewportWidth || numberOfCards <= 1) {
          fitCardsWidth = cardsWidthWithMargins;
          numberOfCards += 2;
        } else {
          break;
        }
      }
      edgeCardVisibleWidth = Math.max((viewportWidth - fitCardsWidth) / 2, 0);
      if (this.isSmallDesktopViewPortWidth) {
        edgeCardVisibleWidth = 0;
      }
      cardsContainerMargin = edgeCardVisibleWidth + cardMargin;
    }

    cardsContainer.style.marginLeft = `${cardsContainerMargin}px`;
    cardsContainer.style.marginRight = `${cardsContainerMargin}px`;

    let firstCardFits = cardsFit || allCards[0].getBoundingClientRect().x - cardMargin > 0;
    if (this.isSmallDesktopViewPortWidth) {
      firstCardFits = cardsFit || allCards[0].getBoundingClientRect().x > 0;
    }
    let lastCardFits = cardsFit || allCards[allCards.length - 1].getBoundingClientRect().x < viewportWidth - cardWidth - cardMargin;
    if (this.isSmallDesktopViewPortWidth) {
      lastCardFits = cardsFit || allCards[allCards.length - 1].getBoundingClientRect().x < viewportWidth - cardWidth;
    }

    this.changeVisibility(this.leftButton, !firstCardFits);
    this.changeVisibility(this.rightButton, !lastCardFits);

    const overlayHeightOffset = 5;
    const overlayHeight = allCards[0].getBoundingClientRect().height + overlayHeightOffset * 2;

    const svgWidth = 40;
    const arrowHorizontalOffset = edgeCardVisibleWidth + cardMargin - svgWidth / 2;
    document.getElementById('left-arrow-button').style.left = `${arrowHorizontalOffset}px`;
    document.getElementById('right-arrow-button').style.right = `${arrowHorizontalOffset}px`;
    document.getElementById('left-arrow-button').style.marginTop = `${overlayHeight / 4}px`;
    document.getElementById('right-arrow-button').style.marginTop = `${overlayHeight / 4}px`;

    document.getElementById('left-gradient-overlay').style.height = `${overlayHeight}px`;
    document.getElementById('right-gradient-overlay').style.height = `${overlayHeight}px`;
    document.getElementById('left-gradient-overlay').style.marginTop = `-${overlayHeightOffset}px`;
    document.getElementById('right-gradient-overlay').style.marginTop = `-${overlayHeightOffset}px`;
    document.getElementById('left-gradient-overlay').style.width = `${edgeCardVisibleWidth}px`;
    document.getElementById('right-gradient-overlay').style.width = `${edgeCardVisibleWidth}px`;
    this.viewPortWidth = viewportWidth;
    this.cardsContainerWidth = cardsContainerWidth;
    this.cardWidth = cardWidth;
    this.overlayWidth = edgeCardVisibleWidth;
    this.cardsFit = cardsFit;
    this.scrollLeft = carouselWrapper.scrollLeft;
    this.firstCardFits = firstCardFits;
    this.lastCardFits = lastCardFits;
  };

  private get isSmallDesktopViewPortWidth(): boolean {
    const carouselWrapper = document.getElementById('carousel-wrapper');
    if (!carouselWrapper) return;
    const containerBounding = carouselWrapper.getBoundingClientRect();
    const viewportWidth = containerBounding.width;
    return viewportWidth < 1300 && viewportWidth > 800;
  }

  private onScroll = (): void => {
    this.calculateAndPositionCards();
  };

  componentDidLoad(): void {
    this.leftButton = this.hostElement.querySelector('.left-button');
    this.rightButton = this.hostElement.querySelector('.right-button');
    this.calculateAndPositionCards();
    this.focusedCardIndex && this.focusInitialCard();
  }

  @Watch('numberOfProducts')
  public watchHandler(): void {
    setTimeout(() => {
      this.calculateAndPositionCards();
    });
  }

  componentWillLoad(): void {
    window.addEventListener('resize', this.onResize, true);
    window.addEventListener('scroll', this.onScroll, true);
  }

  private onResize = () => {
    this.calculateAndPositionCards();
  };

  render(): JSX.Element {
    return (
      <Host>
        <div class="carousel-wrapper scroll-snap-type" id="carousel-wrapper">
          <div id="left-gradient-overlay" class="gradient-overlay left-gradient-overlay" onClick={() => this.moveLeft()} />
          <button id="left-arrow-button" class="left-button" aria-label="Move left" onClick={() => this.moveLeft()}>
            <div class="svg-container" innerHTML={LeftArrow} />
          </button>
          <button id="right-arrow-button" class="right-button" aria-label="Move right" onClick={() => this.moveRight()}>
            <div class="svg-container" innerHTML={RightArrow} />
          </button>
          <div id="right-gradient-overlay" class="gradient-overlay right-gradient-overlay" onClick={() => this.moveRight()} />
          <slot />
        </div>
        {this.showPagination && this.renderPagination()}
      </Host>
    );
  }

  private moveRight(): void {
    const carouselScroller = document.querySelector('#carousel-wrapper');
    const cardWidth = carouselScroller.querySelector('.card__wrapper').clientWidth;
    carouselScroller.scrollBy(cardWidth, 0);
  }

  private moveLeft(): void {
    const carouselScroller = document.querySelector('#carousel-wrapper');
    const cardWidth = carouselScroller.querySelector('.card__wrapper').clientWidth;
    carouselScroller.scrollBy(-cardWidth, 0);
  }

  private scrollToPageIndex(activePage: number, targetPage: number): void {
    if (activePage !== targetPage) {
      const carouselScroller = document.querySelector('#carousel-wrapper');
      const cardWidth = carouselScroller.querySelector('.card__wrapper').clientWidth;
      carouselScroller.scrollBy((targetPage - activePage) * cardWidth, 0);
    }
  }

  private renderPagination(): JSX.Element {
    if (!this.cardsFit) {
      const allCards = document.getElementsByClassName('card__wrapper');
      const visibleContainerWidth = this.viewPortWidth - 2 * this.overlayWidth;
      const hiddenCards = Math.ceil((this.cardsContainerWidth - visibleContainerWidth) / this.cardWidth);
      const numberOfPage = allCards.length == hiddenCards ? hiddenCards : hiddenCards + 1;
      let activePage = 0;
      if (this.firstCardFits) {
        activePage = 0;
      } else if (this.lastCardFits) {
        activePage = numberOfPage - 1;
      } else {
        activePage = Math.floor(this.scrollLeft / this.cardWidth);
        if (activePage === 0) activePage = 1;
      }

      const paginations = [];
      for (let i = 0; i < numberOfPage; i++) {
        paginations.push(
          <span
            class={i === activePage ? 'pagination-bullet-active' : 'pagination-bullet'}
            onClick={() => this.scrollToPageIndex(activePage, i)}
          />
        );
      }

      const paginationStyle = { width: `${this.viewPortWidth}px` };

      return (
        <div class="pagination-container" style={paginationStyle}>
          {paginations}
        </div>
      );
    }

    return <div />;
  }
}
